import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';
import ApplyBadge from './svg/apply-badge.svg';
import BuildingBadge from './svg/building-badge.svg';
import StarBadge from './svg/star-badge.svg';
const badgeVariants = cva(['rounded-xsmall', 'px-6 py-2', 'font-600', 'inline-flex items-center gap-4', 'bg-Gray-200 text-Gray-700'], {
  variants: {
    dimensions: {
      H24: 'typo-caption1',
      H20: 'typo-caption2'
    }
  },
  defaultVariants: {
    dimensions: 'H24'
  }
});
interface BadgeProps extends ComponentPropsWithoutRef<'span'>, VariantProps<typeof badgeVariants> {
  icon?: 'building' | 'star' | 'apply';
  asChild?: boolean;
}
const Badge = forwardRef<HTMLSpanElement, BadgeProps>((t0, ref) => {
  const $ = _c(21);
  let asChild;
  let children;
  let className;
  let dimensions;
  let icon;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      icon,
      dimensions,
      children,
      asChild,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = className;
    $[4] = dimensions;
    $[5] = icon;
    $[6] = props;
  } else {
    asChild = $[1];
    children = $[2];
    className = $[3];
    dimensions = $[4];
    icon = $[5];
    props = $[6];
  }
  const Icon = icon === "building" ? BuildingBadge : icon === "star" ? StarBadge : null;
  if (icon === "apply") {
    let t1;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ApplyBadge />;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  const Comp = asChild ? Slot : "span";
  let t1;
  if ($[8] !== className || $[9] !== dimensions) {
    t1 = cn(badgeVariants({
      dimensions
    }), className);
    $[8] = className;
    $[9] = dimensions;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== Icon || $[12] !== asChild || $[13] !== children) {
    t2 = asChild ? children : <>{Icon ? <Icon /> : null}{children}</>;
    $[11] = Icon;
    $[12] = asChild;
    $[13] = children;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== Comp || $[16] !== props || $[17] !== ref || $[18] !== t1 || $[19] !== t2) {
    t3 = <Comp ref={ref} className={t1} {...props}>{t2}</Comp>;
    $[15] = Comp;
    $[16] = props;
    $[17] = ref;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
});
Badge.displayName = 'Badge';
export { Badge, type BadgeProps };